import moment from 'moment';
import get from 'lodash/get';
import {useEffect,useState} from 'react';
import {useNavigate,useParams,useSearchParams} from 'react-router-dom';
import {useDispatch} from 'react-redux';

import Container from '@mui/material/Container';

import useError from '../../hooks/useError';
import useUser from '../../hooks/useUser';
import {getDefaultCase} from '../../helpers/cases';
import {reset} from '../../redux/reducers/newAppointmentForm';
import {useGetCasesQuery} from '../../redux/services/caseApi';
import {useGetPatientsQuery} from '../../redux/services/patientApi';
import {useGetProvidersQuery} from '../../redux/services/providerApi';
import {useGetSessionTypesQuery} from '../../redux/services/sessionTypeApi';
import {useCreateAppointmentMutation} from '../../redux/services/appointmentApi';
import { setScreenIndex, setSessionTypeId } from '../../redux/reducers/newAppointmentForm';


import AppDashboard from '../AppDashboard';
import NewAppointmentForm from '../../components/scheduling/NewAppointmentForm';

const parseSearchParams = search => {
  const screenIndex = parseInt(search.get('screenIndex'), 10) || null;
  const sessionTypeId = parseInt(search.get('sessionTypeId'), 10) || null;
  return {screenIndex, sessionTypeId};
};

const handleSubmit = ({
  createAppointment,
  onSuccess,
  setError,
  resetForm,
  selectedProviderId,
  sessionTypeId,
  selectedPatientId,
  caseId,
}) => async formValues => {
  const body = {
    patient_id: selectedPatientId,
    session_type_id: formValues.sessionTypeId,
    start_time: moment(
      `${formValues.date} ${formValues.time}`,
      'MM/DD/yyyy hh:mm a'
    ).toISOString(),
    case_id: formValues.case_id || caseId,
  };
  const res = await createAppointment(body);
  if (res.error) return setError({message: 'Error scheduling appointment.'});
  resetForm();
  const appointmentId = get(res, 'data.id');
  if (appointmentId) onSuccess(res.data.id);
};

const PatientAppointmentPage = props => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {create: setError} = useError();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {id} = useParams();
  useEffect(() => {
    dispatch(reset());
    const {screenIndex, sessionTypeId} = parseSearchParams(searchParams);
    if (screenIndex) dispatch(setScreenIndex(screenIndex));
    if (sessionTypeId) dispatch(setSessionTypeId(sessionTypeId));
  }, []);
  const patientId = parseInt(id, 10);
  const user = useUser();
  const provider = user?.provider;
  const providerId = provider?.id;
  const sessionTypesQuery = useGetSessionTypesQuery(
    {providerId: providerId},
    {skip: !providerId}
  );
  const patientsQuery = useGetPatientsQuery(
    {id: patientId, hide_details: true},
    {skip: !patientId}
  );
  const selectedPatient = (get(patientsQuery, 'data.data') || [])
      .find(p => p.id === patientId);
  const casesQuery = useGetCasesQuery(
    {patient_id: selectedPatient?.id},
    {skip: !selectedPatient?.id}
  );
  const defaultCase = getDefaultCase(casesQuery?.data?.data || [], {providerId});
  const [createAppointment] = useCreateAppointmentMutation();
  const onSuccess = (appointmentId) => navigate(`/appointments/success/${appointmentId}`);
  const childProps = {
    ...props,
    onSubmit: handleSubmit({
      caseId: defaultCase?.id,
      selectedProviderId: providerId,
      selectedPatientId: patientId,
      createAppointment,
      onSuccess,
      setError,
      resetForm: () => dispatch(reset()),
    }),
    // data
    selectedPatient,
    selectedProvider: provider,
    defaultCaseId: defaultCase?.id,
    sessionTypes: get(sessionTypesQuery, 'data', []),
    // config
    loading: !user || sessionTypesQuery.isLoading || patientsQuery.isLoading,
    error: sessionTypesQuery.error,
    selectedProviderId: providerId,
    hideManualScheduling: true,
    scheduleManually: false,
    hideStepper: true,
    onPrevScreen: null,
    isProvider: true,
  };
  return (
    <AppDashboard loading={childProps.loading} error={childProps.error}>
      <Container maxWidth='sm'>
        <NewAppointmentForm {...childProps} backButtonPlacement='top'/>
      </Container>
    </AppDashboard>
  );
};

export default PatientAppointmentPage;
